import { Injectable } from '@angular/core';
import { Question } from '../models/question';
import { Answer } from '../models/answer';
import { QuestionPage } from '../models/question_page';
import { Survey } from '../models/survey';

/**
 * Service for O(1) lookups of survey data structures
 * Eliminates repeated flatMap().find() operations
 * 
 * @author AI Assistant
 * @date 2026-01-07
 * @see P0-03 Performance optimization plan
 */
@Injectable({ providedIn: 'root' })
export class SurveyIndexService {
  private questionById = new Map<number, Question>();
  private questionByQN = new Map<number, Question>();
  private answerByQuestionId = new Map<number, Answer>();
  private pageByQuestionId = new Map<number, QuestionPage>();
  private dependentsByQuestionId = new Map<number, Set<number>>();
  
  /**
   * Rebuild all indexes when survey data changes
   * Call this after survey load or reset
   * 
   * @param survey - Survey data structure
   * @param answers - Array of current answers
   */
  rebuildIndexes(survey: Survey, answers: Answer[]): void {
    this.questionById.clear();
    this.questionByQN.clear();
    this.answerByQuestionId.clear();
    this.pageByQuestionId.clear();
    this.dependentsByQuestionId.clear();
    
    if (!survey?.pages) {
      return;
    }
    
    // Index questions and their parent pages
    for (const page of survey.pages) {
      if (!page.questions) continue;
      
      for (const question of page.questions) {
        this.questionById.set(question.id as number, question);
        this.pageByQuestionId.set(question.id as number, page);
        if (typeof question.q_n === 'number') {
          this.questionByQN.set(question.q_n, question);
        }
      }
    }
    
    // Index answers by question_id
    if (answers) {
      for (const answer of answers) {
        this.answerByQuestionId.set(answer.question_id, answer);
      }
    }

    this.buildDependentsIndex(survey);
  }
  
  /**
   * Get question by ID - O(1) lookup
   * @param id - Question identifier
   * @returns Question or undefined if not found
   */
  getQuestion(id: number): Question | undefined {
    return this.questionById.get(id);
  }

  /**
   * Get question by creation order - O(1) lookup
   * @param qn - Question creation order
   * @returns Question or undefined if not found
   */
  getQuestionByQN(qn: number): Question | undefined {
    return this.questionByQN.get(qn);
  }
  
  /**
   * Get answer for a question - O(1) lookup
   * @param questionId - Question identifier
   * @returns Answer or undefined if not found
   */
  getAnswer(questionId: number): Answer | undefined {
    return this.answerByQuestionId.get(questionId);
  }
  
  /**
   * Get page containing a question - O(1) lookup
   * @param questionId - Question identifier
   * @returns QuestionPage or undefined if not found
   */
  getPage(questionId: number): QuestionPage | undefined {
    return this.pageByQuestionId.get(questionId);
  }

  /**
   * Get dependent question ids that reference a question id in their logic
   * @param questionId - Question identifier
   */
  getDependents(questionId: number): Set<number> {
    return this.dependentsByQuestionId.get(questionId) ?? new Set<number>();
  }
  
  /**
   * Update answer index when answers change
   * More efficient than full rebuild when only answers changed
   * 
   * @param answers - Updated answers array
   */
  updateAnswerIndex(answers: Answer[]): void {
    this.answerByQuestionId.clear();
    
    if (answers) {
      for (const answer of answers) {
        this.answerByQuestionId.set(answer.question_id, answer);
      }
    }
  }
  
  /**
   * Clear all indexes when survey is unloaded
   */
  clearIndexes(): void {
    this.questionById.clear();
    this.questionByQN.clear();
    this.answerByQuestionId.clear();
    this.pageByQuestionId.clear();
    this.dependentsByQuestionId.clear();
  }

  /**
   * Update or insert a single answer in the index
   * @param answer - Answer to upsert
   */
  setAnswer(answer: Answer): void {
    if (!answer) return;
    this.answerByQuestionId.set(answer.question_id, answer);
  }

  /**
   * Remove an answer from the index
   * @param questionId - Question identifier
   */
  removeAnswer(questionId: number): void {
    this.answerByQuestionId.delete(questionId);
  }

  private buildDependentsIndex(survey: Survey): void {
    if (!survey?.pages?.length) return;

    for (const page of survey.pages) {
      if (!page?.questions?.length) continue;

      for (const question of page.questions) {
        this.addDependentsFromLogic(question.id, question.logic_to_show);
        question.alternatives?.forEach((alternative) => {
          this.addDependentsFromLogic(question.id, alternative.logic_to_show);
        });
        this.addDependentsFromCarryForward(question);
      }
    }
  }

  private addDependentsFromLogic(ownerQuestionId: number, logic: any): void {
    const conditions = logic?.conditions;
    if (!conditions || !Array.isArray(conditions)) return;

    conditions.forEach((condition: any) => {
      const rawQuestionId = condition?.question_id;
      const conditionQuestionId = typeof rawQuestionId === 'number' ? rawQuestionId : Number(rawQuestionId);
      if (!Number.isFinite(conditionQuestionId)) return;
      const dependents = this.dependentsByQuestionId.get(conditionQuestionId) ?? new Set<number>();
      dependents.add(ownerQuestionId);
      this.dependentsByQuestionId.set(conditionQuestionId, dependents);
    });
  }

  private addDependentsFromCarryForward(question: Question): void {
    const sourceIdRaw = (question as any)?.copy_alternatives_question_id;
    if (sourceIdRaw == null) return;
    const sourceId = typeof sourceIdRaw === 'number' ? sourceIdRaw : Number(sourceIdRaw);
    if (!Number.isFinite(sourceId)) return;
    const dependents = this.dependentsByQuestionId.get(sourceId) ?? new Set<number>();
    dependents.add(question.id as number);
    this.dependentsByQuestionId.set(sourceId, dependents);
  }
  
  /**
   * Get all indexed questions
   * @returns Iterator of all questions
   */
  getAllQuestions(): IterableIterator<Question> {
    return this.questionById.values();
  }
  
  /**
   * Check if indexes are populated
   * @returns true if indexes contain data
   */
  hasIndexes(): boolean {
    return this.questionById.size > 0;
  }
}
